import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, BookOpen, Layers, Eye } from 'lucide-react';
import { fetchPlaybooks } from '../lib/api';
import type { PlaybookSummary } from '../types/wizard';
import PlaybookPhaseTracker from '../components/PlaybookPhaseTracker';

/* ------------------------------------------------------------------ */
/*  Utility: fade-in-on-scroll wrapper                                */
/* ------------------------------------------------------------------ */
function FadeIn({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Playbook Card Component                                            */
/* ------------------------------------------------------------------ */
function PlaybookCard({ playbook, index }: { playbook: PlaybookSummary; index: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <FadeIn delay={index * 0.05}>
      <div className="card card-hover flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <span className="text-2xl">{playbook.icon}</span>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">{playbook.name}</h3>
            <span className="text-xs text-text-tertiary font-mono">{playbook.projectType}</span>
          </div>
          <span className="flex items-center gap-1 text-xs text-text-tertiary bg-surface-100 px-2 py-1 rounded-full border border-dark-border shrink-0">
            <Layers className="w-3 h-3" />
            {playbook.phases.length} phases
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-400 mb-4 line-clamp-2">{playbook.description}</p>

        {/* Phase preview pills */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {playbook.phases.map((phase, i) => (
            <span
              key={phase.id}
              className="px-2 py-0.5 text-xs rounded bg-surface-100 text-gray-400 border border-dark-border"
            >
              {i + 1}. {phase.name}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-auto pt-3 border-t border-dark-border">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-white transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide Phases
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                View Phases
              </>
            )}
          </button>
          <Link
            to={`/playbooks/${playbook.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent-400 transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            Explore
          </Link>
          <Link
            to={`/wizard?playbook=${playbook.id}`}
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent-400 transition-colors"
          >
            Use this Playbook
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Expanded phase tracker */}
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 pt-4 border-t border-dark-border"
          >
            <PlaybookPhaseTracker playbook={playbook} />
          </motion.div>
        )}
      </div>
    </FadeIn>
  );
}

/* ------------------------------------------------------------------ */
/*  Playbooks Page                                                     */
/* ------------------------------------------------------------------ */
export default function PlaybooksPage() {
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaybooks()
      .then((data) => setPlaybooks(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative min-h-screen pt-28 pb-20">
      {/* Background effects */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-20" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/5 rounded-full blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium mb-6">
            <BookOpen className="h-3.5 w-3.5" />
            Methodology as Code
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
            Project <span className="text-gradient">Playbooks</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Methodology as code -- structured phases with AI-powered guidance for every project type.
            Each playbook provides Claude Code instructions, prompt templates, and deliverables for every phase.
          </p>
        </motion.div>

        {/* Playbook grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-6 w-48 bg-surface-100 rounded mb-3" />
                <div className="h-4 w-full bg-surface-100 rounded mb-2" />
                <div className="h-4 w-3/4 bg-surface-100 rounded mb-4" />
                <div className="flex gap-2">
                  <div className="h-6 w-20 bg-surface-100 rounded" />
                  <div className="h-6 w-20 bg-surface-100 rounded" />
                  <div className="h-6 w-20 bg-surface-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : playbooks.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400">No playbooks available. Make sure the backend is running.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {playbooks.map((playbook, i) => (
              <PlaybookCard key={playbook.id} playbook={playbook} index={i} />
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <FadeIn delay={0.3} className="text-center mt-16">
          <p className="text-gray-400 mb-4">
            Want to start from scratch instead?
          </p>
          <Link to="/wizard" className="btn-primary text-base px-8 py-3">
            Open the Wizard
            <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeIn>
      </div>
    </div>
  );
}
